<template>
  <v-container fluid class="pa-0">
    <!-- Header -->
    <div class="mb-7">
      <div style="font-size: 30px; font-weight: 800">Skills Assessment</div>
      <div class="text-subtitle-2 mt-1">
        <span class="text-grey"
          >Complete all assessments to update your skills profile for internship
          matching</span
        >
      </div>
    </div>

    <!-- Loading -->
    <div v-if="initialLoading" class="d-flex justify-center py-12">
      <v-progress-circular indeterminate color="#10b981" size="48" />
    </div>

    <template v-else>
      <!-- Assessments to take -->
      <template v-if="table_data.length > 0">
        <div
          class=""
          :class="
            $vuetify.display.mdAndUp ? 'd-flex justify-space-between' : ''
          "
        >
          <div class="d-flex align-center gap-2 mb-4">
            <v-icon color="#10b981" size="20"
              >mdi-clipboard-list-outline</v-icon
            >
            <span class="text-subtitle-2 font-weight-bold"
              >Assessments to Take</span
            >
            <v-chip size="x-small" color="#10b981" variant="tonal">{{
              table_data.length
            }}</v-chip>
          </div>
          <div class="d-flex align-center gap-2 mb-4">
            <v-icon color="#10b981" size="20">mdi-calendar</v-icon>
            <span class="text-subtitle-2 font-weight-bold"
              >Available Until</span
            >
            <v-chip size="x-small" color="#10b981" variant="tonal">{{
              availability
            }}</v-chip>
          </div>
        </div>
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="4"
            v-for="item in table_data"
            :key="item.id"
          >
            <v-card
              variant="outlined"
              rounded="xl"
              class="assessment-card pa-5 h-100 d-flex flex-column"
            >
              <div class="d-flex align-start gap-3 mb-4">
                <div
                  class="assess-icon-wrap d-flex align-center justify-center rounded-circle flex-shrink-0"
                >
                  <v-icon color="#10b981" size="22"
                    >mdi-clipboard-check-outline</v-icon
                  >
                </div>
                <div class="flex-grow-1">
                  <div class="text-body-1 font-weight-bold">
                    {{ item.title }}
                  </div>
                  <div
                    class="text-caption text-grey mt-1 d-flex align-center gap-1"
                  >
                    <v-icon size="13">mdi-clock-outline</v-icon>
                    {{
                      item.time_limit
                        ? `${item.time_limit} min`
                        : "No time limit"
                    }}
                  </div>
                </div>
              </div>
              <v-spacer />
              <v-btn
                color="#10b981"
                variant="flat"
                class="text-white"
                prepend-icon="mdi-play-circle-outline"
                block
                :disabled="shouldDisableAssessment"
                rounded="lg"
                @click="viewItem(item)"
              >
                Start Assessment
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Completed state -->
      <template v-else>
        <v-card rounded="xl" variant="flat" class="results-card">
          <!-- Hero -->
          <div class="results-hero">
            <div class="d-flex align-center gap-4" style="min-width: 0">
              <div class="seal-wrap flex-shrink-0" style="background: #fff">
                <v-icon size="36" color="#10b981">mdi-seal</v-icon>
              </div>
              <div>
                <div class="results-title">Assessment Completed</div>
                <div class="results-sub">
                  Your skills profile has been updated for job matching.
                </div>
              </div>
            </div>

            <div class="score-ring-wrap">
              <v-progress-circular
                :model-value="overallNum"
                :size="104"
                :width="10"
                color="#10b981"
                bg-color="#d1fae5"
              >
                <span class="ring-value">{{ overall ?? "—" }}</span>
              </v-progress-circular>
              <div class="ring-label">Overall Score</div>
            </div>
          </div>

          <div class="pa-6">
            <!-- Stats -->
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="stat-tile">
                  <div class="stat-tile-icon" style="background: #ecfdf5">
                    <v-icon color="#10b981" size="22"
                      >mdi-check-circle-outline</v-icon
                    >
                  </div>
                  <div>
                    <div class="stat-tile-value">{{ correct_ans ?? "—" }}</div>
                    <div class="stat-tile-label">Correct Answers</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="stat-tile">
                  <div class="stat-tile-icon" style="background: #eff6ff">
                    <v-icon color="#3b82f6" size="22"
                      >mdi-clipboard-check-outline</v-icon
                    >
                  </div>
                  <div>
                    <div class="stat-tile-value">
                      {{ skill_assessment_cnt ?? "—" }}
                    </div>
                    <div class="stat-tile-label">Assessments Taken</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Recommended track -->
            <div v-if="getRecommendedResult.name" class="best-match mt-4">
              <div class="d-flex align-center gap-2 mb-3">
                <v-icon color="#10b981" size="20"
                  >mdi-star-circle-outline</v-icon
                >
                <span class="best-match-label"
                  >Best Match — Recommended Internship Track</span
                >
              </div>
              <div
                class="d-flex align-center justify-space-between flex-wrap gap-4"
              >
                <div style="min-width: 0; flex: 1">
                  <div class="best-match-name">
                    {{ getRecommendedResult.name }}
                  </div>
                  <div class="text-caption text-grey-darken-1 mt-1">
                    {{ getRecommendedResult.description }}
                  </div>
                </div>
                <div class="text-right flex-shrink-0">
                  <div class="best-match-pct">
                    {{ getRecommendedResult.matchPercentage }}%
                  </div>
                  <div class="text-caption text-grey">Match</div>
                </div>
              </div>
              <v-progress-linear
                :model-value="Number(getRecommendedResult.matchPercentage) || 0"
                color="#10b981"
                bg-color="#d1fae5"
                height="8"
                rounded
                class="mt-3"
              />
            </div>
          </div>
        </v-card>

        <!-- HTE Internship Matching -->
        <div class="mt-8">
          <div
            class="d-flex align-center justify-space-between flex-wrap gap-2 mb-1"
          >
            <div class="d-flex align-center gap-2">
              <v-icon color="#10b981" size="20">{{
                isPlaced ? "mdi-account-check-outline" : "mdi-domain"
              }}</v-icon>
              <span class="text-subtitle-1 font-weight-bold">{{
                hteSectionTitle
              }}</span>
              <v-chip
                v-if="!hteLoading && !isPlaced && hteRecommendations.length > 0"
                size="x-small"
                color="#10b981"
                variant="tonal"
                >{{ hteRecommendations.length }}</v-chip
              >
            </div>
          </div>
          <div class="text-caption text-grey mb-4">
            {{ hteSectionSubtitle }}
          </div>

          <!-- Loading -->
          <div v-if="hteLoading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="#10b981" size="36" />
          </div>

          <!-- Placement (Deployed / Completed) -->
          <v-card
            v-else-if="isPlaced && hteRecommendations.length > 0"
            rounded="xl"
            variant="flat"
            class="placed-card"
          >
            <div class="placed-banner">
              <div class="d-flex align-center gap-4" style="min-width: 0">
                <div class="placed-avatar flex-shrink-0">
                  <v-icon color="white" size="28">mdi-domain</v-icon>
                </div>
                <div style="min-width: 0">
                  <div class="placed-eyebrow">Host Training Establishment</div>
                  <div class="placed-company">
                    {{ placedHte?.internship?.company }}
                  </div>
                  <div
                    v-if="placedHte?.internship?.address"
                    class="placed-address d-flex align-center gap-1"
                  >
                    <v-icon size="14" color="white"
                      >mdi-map-marker-outline</v-icon
                    >
                    <span>{{ placedHte.internship.address }}</span>
                  </div>
                </div>
              </div>
              <div class="status-badge">
                <v-icon size="26" color="#059669">{{ placementIcon }}</v-icon>
                <div>
                  <div class="status-badge-eyebrow">Current Status</div>
                  <div class="status-badge-label">{{ placementLabel }}</div>
                </div>
              </div>
            </div>

            <div class="pa-5">
              <!-- Status progress -->
              <div class="status-steps mb-5">
                <div
                  v-for="st in statusSteps"
                  :key="st.label"
                  class="status-step"
                  :class="{
                    'status-step--done': st.state === 'done',
                    'status-step--current': st.state === 'current',
                  }"
                >
                  <div class="status-step-dot">
                    <v-icon v-if="st.state === 'done'" size="15" color="white"
                      >mdi-check</v-icon
                    >
                    <v-icon
                      v-else-if="st.state === 'current'"
                      size="15"
                      color="white"
                      >{{ st.icon }}</v-icon
                    >
                  </div>
                  <div class="status-step-label">{{ st.label }}</div>
                </div>
              </div>

              <v-row v-if="placementDetails.length > 0" dense>
                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-for="d in placementDetails"
                  :key="d.label"
                >
                  <div class="placed-tile">
                    <div class="placed-tile-icon">
                      <v-icon size="16" color="#10b981">{{ d.icon }}</v-icon>
                    </div>
                    <div style="min-width: 0">
                      <div class="hte-skill-label">{{ d.label }}</div>
                      <div class="placed-tile-value text-truncate">
                        {{ d.value }}
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <div
                v-if="placement && placement.hours_required && !isEndorsed"
                class="mt-5"
              >
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="text-caption font-weight-bold text-grey-darken-1"
                    >Training Hours</span
                  >
                  <span class="text-caption font-weight-bold"
                    >{{ placement.hours_rendered ?? 0 }} /
                    {{ placement.hours_required }} hrs</span
                  >
                </div>
                <v-progress-linear
                  :model-value="hoursPct"
                  color="#10b981"
                  bg-color="#d1fae5"
                  height="10"
                  rounded
                />
                <div class="text-caption text-grey mt-1 text-right">
                  {{ hoursPct }}% complete
                </div>
              </div>
            </div>
          </v-card>

          <!-- List -->
          <v-row v-else-if="!isPlaced && hteRecommendations.length > 0">
            <v-col
              cols="12"
              md="6"
              v-for="(item, idx) in hteRecommendations"
              :key="item.id || idx"
            >
              <v-card
                variant="outlined"
                rounded="xl"
                class="hte-card pa-4 h-100"
                :class="{
                  'hte-card--top': !isPlaced && idx === 0,
                  'hte-card--placed': isPlaced,
                }"
              >
                <div class="d-flex justify-space-between align-start gap-3">
                  <div class="d-flex align-center gap-3" style="min-width: 0">
                    <div class="hte-icon flex-shrink-0">
                      <v-icon size="18" color="#6b7280">mdi-domain</v-icon>
                    </div>
                    <div style="min-width: 0">
                      <div class="hte-company">
                        {{ item.internship?.company }}
                      </div>
                      <div
                        v-if="item.internship?.address"
                        class="text-caption text-grey d-flex align-center gap-1"
                      >
                        <v-icon size="12">mdi-map-marker-outline</v-icon>
                        <span class="text-truncate">{{
                          item.internship.address
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="text-right flex-shrink-0">
                    <div
                      v-if="!isPlaced"
                      class="hte-match"
                      :style="
                        !isPlaced && idx === 0
                          ? 'color:#d97706'
                          : 'color:#10b981'
                      "
                    >
                      {{ item.matchPercentage }}% Match
                    </div>
                    <div
                      v-if="
                        !isPlaced && item.internship?.slotsAvailable != null
                      "
                      class="text-caption text-grey"
                    >
                      Slots:
                      <strong>{{ item.internship.slotsAvailable }}</strong>
                    </div>
                  </div>
                </div>

                <div v-if="!isPlaced" class="mt-3">
                  <div class="hte-skill-label">Skill Match</div>
                  <div
                    v-if="item.matchedSkills && item.matchedSkills.length > 0"
                    class="mt-1 d-flex flex-wrap gap-1"
                  >
                    <v-chip
                      size="x-small"
                      color="green"
                      v-for="s in item.matchedSkills"
                      :key="s"
                      >{{ s }}</v-chip
                    >
                  </div>
                  <div v-else class="text-caption text-grey mt-1">
                    No matching skills yet
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Empty -->
          <v-card
            v-else
            variant="outlined"
            rounded="xl"
            class="pa-6 text-center"
          >
            <v-icon size="36" color="#d1d5db">mdi-domain-off</v-icon>
            <div class="text-body-2 text-grey mt-2">
              No recommended HTEs available yet.
            </div>
          </v-card>
        </div>
      </template>
    </template>

    <!-- Dialogs -->
    <StudentSkillAssessmentDialog :data="itemData" :action="action" />

    <v-dialog v-model="dialogConfirmDelete" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Confirm Deletion</v-card-title>
        <v-card-text class="text-body-1"
          >Are you sure you want to delete this item?</v-card-text
        >
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogConfirmDelete = false"
            >Cancel</v-btn
          >
          <v-btn color="error" @click="confirmDelete">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </v-container>
</template>

<script>
import eventBus from "@/eventBus";
import StudentSkillAssessmentDialog from "../Dialogs/Forms/SkillAssessment/StudentSkillAssessmentDialog.vue";

export default {
  components: { StudentSkillAssessmentDialog },

  data: () => ({
    initialLoading: false,
    loading: false,
    table_data: [],
    results: [],
    overall: null,
    correct_ans: null,
    skill_assessment_cnt: null,
    itemData: null,
    action: null,
    deleteData: null,
    dialogConfirmDelete: false,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "Successfully Deleted!",
      message: "",
      top: 10,
    },
    availability: null,
    sg: null,

    // HTE internship matching
    hteLoading: false,
    hteRecommendations: [],
    // InternshipStatusEnum: 1 Requested, 2 Endorsed, 3 Deployed,
    // 4 Completed, 5 Pullout, 6 Terminated
    internshipStatus: null,
    placement: null,
  }),

  computed: {
    getRecommendedResult() {
      if (this.results && this.results.length > 0) {
        return this.results[0];
      }
      return { name: null, description: null, matchPercentage: null };
    },

    shouldDisableAssessment() {
      const now = new Date();

      if (this.sg) {
        const datePart = this.sg.date_until;
        const timePart = this.sg.time_until;

        const concatenatedString = `${datePart}T${timePart}`;
        const secondDateTime = new Date(concatenatedString);
        return secondDateTime.getTime() < now.getTime();
      }
      return true;
    },

    // Endorsed (2), Deployed (3) or Completed (4): show only their HTE
    isPlaced() {
      return [2, 3, 4].includes(this.internshipStatus);
    },

    isEndorsed() {
      return this.internshipStatus === 2;
    },

    overallNum() {
      const n = parseFloat(String(this.overall ?? "").replace("%", ""));
      return isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
    },

    placedHte() {
      return this.hteRecommendations[0] || null;
    },

    // Only tiles that have a value are shown
    placementDetails() {
      const p = this.placement || {};
      const rows = [
        {
          icon: "mdi-account-tie-outline",
          label: "Supervisor",
          value: p.contact_person,
        },
        { icon: "mdi-email-outline", label: "Email", value: p.email },
        {
          icon: "mdi-send-check-outline",
          label: "Endorsed On",
          value: p.endorsed_date ? this.formatDate(p.endorsed_date) : null,
        },
        {
          icon: "mdi-calendar-check-outline",
          label: "Deployed On",
          value: p.deployed_date ? this.formatDate(p.deployed_date) : null,
        },
        {
          icon: "mdi-flag-checkered",
          label: "Completed On",
          value: p.completed_date ? this.formatDate(p.completed_date) : null,
        },
      ];
      return rows.filter((r) => r.value);
    },

    hoursPct() {
      const p = this.placement;
      if (!p || !p.hours_required) return 0;
      return Math.min(
        100,
        Math.round(((p.hours_rendered || 0) / p.hours_required) * 100),
      );
    },

    placementIcon() {
      if (this.internshipStatus === 2) return "mdi-send-check-outline";
      if (this.internshipStatus === 4) return "mdi-flag-checkered";
      return "mdi-briefcase-check-outline";
    },

    statusSteps() {
      const steps = [
        { value: 2, label: "Endorsed", icon: "mdi-send-check-outline" },
        { value: 3, label: "Deployed", icon: "mdi-briefcase-check-outline" },
        { value: 4, label: "Completed", icon: "mdi-flag-checkered" },
      ];
      return steps.map((st) => ({
        ...st,
        state:
          this.internshipStatus > st.value
            ? "done"
            : this.internshipStatus === st.value
            ? "current"
            : "pending",
      }));
    },

    placementLabel() {
      if (this.internshipStatus === 2) return "Endorsed";
      if (this.internshipStatus === 4) return "Completed";
      return "Deployed";
    },

    hteSectionTitle() {
      return this.isPlaced
        ? "Your Host Training Establishment"
        : "Recommended Internship Placements";
    },

    hteSectionSubtitle() {
      if (this.internshipStatus === 2) {
        return "You have been endorsed to this Host Training Establishment. Deployment will follow once confirmed.";
      }
      if (this.isPlaced) {
        return this.internshipStatus === 4
          ? "You completed your internship at this Host Training Establishment."
          : "You are currently deployed to this Host Training Establishment.";
      }
      return "Host Training Establishments that best match your skills profile.";
    },
  },

  mounted() {
    this.initialize();
    eventBus.on("closeStudentSkillAssessmentDialog", () => {
      this.initialize();
    });
  },

  beforeUnmount() {
    eventBus.off("closeStudentSkillAssessmentDialog");
  },

  methods: {
    initialize() {
      this.initialLoading = true;
      this.loading = true;
      this.axiosCall("/skill-assessement/student/getToAssess/1", "GET")
        .then((res) => {
          if (res) {
            this.table_data = res.data.data;
            this.sg = res.data.sg;
            if (this.sg) {
              this.availability =
                this.formatDate(res.data.sg.date_until) +
                " " +
                this.formatTime(res.data.sg.time_until);
            }
            if (res.data.data.length === 0) {
              this.loadResult();
              this.loadHteRecommendations();
            }
          }
        })
        .finally(() => {
          this.initialLoading = false;
          this.loading = false;
        });
    },

    loadResult() {
      this.axiosCall("/skill-assessement/student/getToAssess/2", "GET").then(
        (res) => {
          if (res) {
            this.results = res.data.data;
            this.overall = res.data.overall_score;
            this.correct_ans = res.data.correct_answers;
            this.skill_assessment_cnt = res.data.skill_assessment_count;
          }
        },
      );
    },

    // Expected response:
    // {
    //   internship_status: number | null,
    //   data: [
    //     {
    //       id, matchPercentage, matchedSkills: [],
    //       internship: { company, address?, slotsAvailable }
    //     }
    //   ]
    // }
    // Endorsed/Deployed/Completed: `data` holds only the student's HTE.
    loadHteRecommendations() {
      this.hteLoading = true;
      this.axiosCall("/student/getMyHteRecommendations", "GET")
        .then((res) => {
          if (res) {
            console.log(res.data);
            this.internshipStatus = res.data.internship_status ?? null;
            this.placement = res.data.placement || null;
            let list = res.data.data || [];
            // Safety net: deployed/completed students only ever see one HTE
            if (this.isPlaced && list.length > 1) {
              list = list.slice(0, 1);
            }
            this.hteRecommendations = list;
          }
        })
        .catch(() => {
          this.hteRecommendations = [];
          this.internshipStatus = null;
          this.placement = null;
        })
        .finally(() => {
          this.hteLoading = false;
        });
    },

    viewItem(item) {
      this.itemData = item;
      this.action = "View";
    },

    confirmDelete() {
      this.axiosCall("/skill-assessement/" + this.deleteData.id, "DELETE").then(
        () => {
          this.fadeAwayMessage.show = true;
          this.itemData = null;
          this.initialize();
        },
      );
    },
  },
};
</script>

<style scoped>
.assessment-card {
  transition: box-shadow 0.2s;
}
.assessment-card:hover {
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.12) !important;
}

.assess-icon-wrap {
  width: 44px;
  height: 44px;
  min-width: 44px;
  background: #ecfdf5;
}

.seal-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* HTE cards */
.hte-card {
  border-color: #e5e7eb !important;
  background: #fff;
  transition: box-shadow 0.2s;
}
.hte-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.hte-card--top {
  border-color: #fde68a !important;
  background: #fffbeb;
}
.hte-card--placed {
  border-color: #bbf7d0 !important;
  background: #f0fdf4;
}

/* Placement card (Deployed / Completed) */
.placed-card {
  border: 1px solid #a7f3d0;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(16, 185, 129, 0.1);
}
.placed-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
}
.status-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px 10px 14px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
}
.status-badge-eyebrow {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #6b7280;
}
.status-badge-label {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
  color: #047857;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* Status progress */
.status-steps {
  display: flex;
}
.status-step {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.status-step::after {
  content: "";
  position: absolute;
  top: 14px;
  left: calc(50% + 20px);
  right: calc(-50% + 20px);
  height: 3px;
  border-radius: 2px;
  background: #e5e7eb;
}
.status-step:last-child::after {
  display: none;
}
.status-step--done::after {
  background: #10b981;
}
.status-step-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}
.status-step--done .status-step-dot {
  background: #10b981;
}
.status-step--current .status-step-dot {
  background: #10b981;
  box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.22);
}
.status-step-label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
}
.status-step--done .status-step-label {
  color: #059669;
}
.status-step--current .status-step-label {
  color: #047857;
  font-size: 13px;
  font-weight: 800;
}
.placed-avatar {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}
.placed-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  opacity: 0.85;
}
.placed-company {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin-top: 2px;
}
.placed-address {
  font-size: 13px;
  opacity: 0.9;
  margin-top: 4px;
}
.placed-tile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fafafa;
  height: 100%;
}
.placed-tile-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.placed-tile-value {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.hte-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hte-company {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.hte-match {
  font-size: 14px;
  font-weight: 700;
}
.hte-skill-label {
  font-size: 10px;
  text-transform: uppercase;
  color: #9ca3af;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Results card */
.results-card {
  border: 1px solid #e5e7eb;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}
.results-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
  padding: 24px 28px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-bottom: 1px solid #a7f3d0;
}
.results-title {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.3px;
}
.results-sub {
  font-size: 14px;
  color: #4b5563;
  margin-top: 2px;
}
.score-ring-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.ring-value {
  font-size: 17px;
  font-weight: 800;
  color: #059669;
}
.ring-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #047857;
}
.stat-tile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  height: 100%;
}
.stat-tile-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-tile-value {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
}
.stat-tile-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-top: 2px;
}
.best-match {
  border: 1.5px solid #10b981;
  background: #f0fdf4;
  border-radius: 14px;
  padding: 18px;
}
.best-match-label {
  font-size: 13px;
  font-weight: 700;
  color: #059669;
}
.best-match-name {
  font-size: 17px;
  font-weight: 800;
  color: #111827;
  text-transform: uppercase;
}
.best-match-pct {
  font-size: 32px;
  font-weight: 800;
  color: #10b981;
  line-height: 1;
}

.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
.gap-4 {
  gap: 16px;
}
</style>
